import { useEffect, useState } from 'react'
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  LexicalCommand,
} from 'lexical'
import { $generateHtmlFromNodes } from '@lexical/html'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  SuggestionNode,
  $createSuggestionNode,
  $isSuggestionNode,
  $applySuggestion,
  $rejectSuggestion,
  SuggestionData,
} from '../nodes/SuggestionNode'
import { SuggestionPopover } from '../components/SuggestionPopover'

export interface CreateSuggestionPayload {
  suggestedHtml: string // Required HTML content for rich text suggestions
  suggestionId?: string // Optional ID to use instead of generating one
}

export interface SuggestionActionPayload {
  suggestionId: string
}

export const CREATE_SUGGESTION_COMMAND: LexicalCommand<CreateSuggestionPayload> =
  createCommand('CREATE_SUGGESTION_COMMAND')

export const APPLY_SUGGESTION_COMMAND: LexicalCommand<SuggestionActionPayload> =
  createCommand('APPLY_SUGGESTION_COMMAND')

export const REJECT_SUGGESTION_COMMAND: LexicalCommand<SuggestionActionPayload> =
  createCommand('REJECT_SUGGESTION_COMMAND')

export const TOGGLE_SUGGESTION_COMMAND: LexicalCommand<SuggestionActionPayload> =
  createCommand('TOGGLE_SUGGESTION_COMMAND')

export const APPLY_ALL_SUGGESTIONS_COMMAND: LexicalCommand<void> = createCommand('APPLY_ALL_SUGGESTIONS_COMMAND')

export const REJECT_ALL_SUGGESTIONS_COMMAND: LexicalCommand<void> = createCommand('REJECT_ALL_SUGGESTIONS_COMMAND')

interface SuggestionPluginProps {
  isEnabled?: boolean
}

export function SuggestionPlugin({ isEnabled = true }: SuggestionPluginProps): JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  const [activeSuggestion, setActiveSuggestion] = useState<{
    node: SuggestionNode
    data: SuggestionData
    position: { x: number; y: number }
  } | null>(null)

  useEffect(() => {
    if (!isEnabled) {
      return
    }

    const removeCreateCommand = editor.registerCommand(
      CREATE_SUGGESTION_COMMAND,
      (payload: CreateSuggestionPayload) => {
        let suggestionId: string | null = null

        editor.update(() => {
          const selection = $getSelection()

          if (!$isRangeSelection(selection) || selection.isCollapsed()) {
            return
          }

          // Capture the original HTML from the current selection
          const originalHtml = $generateHtmlFromNodes(editor, selection)

          // Extract blockId from the original content
          const extractBlockIdFromSelection = (): string | null => {
            const anchorNode = selection.anchor.getNode()
            const focusNode = selection.focus.getNode()

            const anchorElement = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()
            const focusElement = focusNode.getKey() === 'root' ? focusNode : focusNode.getTopLevelElementOrThrow()

            if (anchorElement.getKey() === focusElement.getKey() && $isElementNode(anchorElement))
              return (anchorElement as any).__blockId || (focusElement as any).__blockId

            return null
          }

          const originalBlockId = extractBlockIdFromSelection()

          const suggestionData: SuggestionData = {
            id: payload.suggestionId || `suggestion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            originalHtml: originalHtml,
            suggestedHtml: payload.suggestedHtml,
            originalBlockId: originalBlockId || undefined,
          }

          suggestionId = suggestionData.id

          // create suggestion node
          const suggestionNode = $createSuggestionNode(suggestionData)
          if (!suggestionNode) return

          // remove all nodes in the selection
          selection.getNodes().forEach(n => n.remove())
          const parentNode = selection.getNodes()[0]
          if (!parentNode) return

          // insert suggestion node before the parent node and remove the parent node as well
          parentNode.insertBefore(suggestionNode)
          parentNode.remove()
        })

        return true
      },
      COMMAND_PRIORITY_LOW
    )

    const removeApplyCommand = editor.registerCommand(
      APPLY_SUGGESTION_COMMAND,
      (payload: SuggestionActionPayload) => {
        editor.update(() => {
          // Find the suggestion node within the editor update context
          const nodeMap = editor.getEditorState()._nodeMap
          let suggestionNode: SuggestionNode | null = null

          nodeMap.forEach(node => {
            if ($isSuggestionNode(node) && node.getSuggestionData().id === payload.suggestionId) {
              suggestionNode = node
            }
          })

          if (suggestionNode) {
            $applySuggestion(suggestionNode)
          }
        })
        setActiveSuggestion(null)
        return true
      },
      COMMAND_PRIORITY_LOW
    )

    const removeRejectCommand = editor.registerCommand(
      REJECT_SUGGESTION_COMMAND,
      (payload: SuggestionActionPayload) => {
        editor.update(() => {
          // Find the suggestion node within the editor update context
          const nodeMap = editor.getEditorState()._nodeMap
          let suggestionNode: SuggestionNode | null = null

          nodeMap.forEach(node => {
            if ($isSuggestionNode(node) && node.getSuggestionData().id === payload.suggestionId) {
              suggestionNode = node
            }
          })

          if (suggestionNode) {
            $rejectSuggestion(suggestionNode)
          }
        })
        setActiveSuggestion(null)
        return true
      },
      COMMAND_PRIORITY_LOW
    )

    const removeToggleCommand = editor.registerCommand(
      TOGGLE_SUGGESTION_COMMAND,
      (payload: SuggestionActionPayload) => {
        editor.getEditorState().read(() => {
          const nodeMap = editor.getEditorState()._nodeMap
          let suggestionNode: SuggestionNode | null = null

          // Find the suggestion node with matching ID
          nodeMap.forEach(node => {
            if ($isSuggestionNode(node)) {
              const suggestionData = node.getSuggestionData()
              if (suggestionData.id === payload.suggestionId) {
                suggestionNode = node
              }
            }
          })

          if (suggestionNode && $isSuggestionNode(suggestionNode)) {
            // @ts-ignore
            const suggestionData = suggestionNode.getSuggestionData()
            const rect = getSuggestionNodeRect(payload.suggestionId)

            if (rect) {
              setActiveSuggestion({
                node: suggestionNode,
                data: suggestionData,
                position: {
                  x: rect.left + rect.width / 2,
                  y: rect.top - 10,
                },
              })
            }
          }
        })
        return true
      },
      COMMAND_PRIORITY_LOW
    )

    const removeApplyAllCommand = editor.registerCommand(
      APPLY_ALL_SUGGESTIONS_COMMAND,
      () => {
        editor.update(() => {
          const nodeMap = editor.getEditorState()._nodeMap
          const suggestionNodes: SuggestionNode[] = []

          // Collect all suggestion nodes
          nodeMap.forEach(node => {
            if ($isSuggestionNode(node)) {
              suggestionNodes.push(node)
            }
          })

          // Apply all suggestions
          suggestionNodes.forEach(suggestionNode => {
            $applySuggestion(suggestionNode)
          })
        })
        setActiveSuggestion(null)
        return true
      },
      COMMAND_PRIORITY_LOW
    )

    const removeRejectAllCommand = editor.registerCommand(
      REJECT_ALL_SUGGESTIONS_COMMAND,
      () => {
        editor.update(() => {
          const nodeMap = editor.getEditorState()._nodeMap
          const suggestionNodes: SuggestionNode[] = []

          // Collect all suggestion nodes
          nodeMap.forEach(node => {
            if ($isSuggestionNode(node)) {
              suggestionNodes.push(node)
            }
          })

          // Reject all suggestions
          suggestionNodes.forEach(suggestionNode => {
            $rejectSuggestion(suggestionNode)
          })
        })
        setActiveSuggestion(null)
        return true
      },
      COMMAND_PRIORITY_LOW
    )

    return () => {
      removeCreateCommand()
      removeApplyCommand()
      removeRejectCommand()
      removeToggleCommand()
      removeApplyAllCommand()
      removeRejectAllCommand()
    }
  }, [editor, isEnabled])

  useEffect(() => {
    if (!isEnabled) {
      return
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const suggestionContainer = target.closest('.suggestion-container')

      if (suggestionContainer) {
        const suggestionId = suggestionContainer.getAttribute('data-suggestion-id')
        if (suggestionId) {
          editor.dispatchCommand(TOGGLE_SUGGESTION_COMMAND, { suggestionId })
        }
      } else {
        // Click outside suggestion - close popover
        setActiveSuggestion(null)
      }
    }

    const editorElement = editor.getRootElement()
    if (editorElement) {
      editorElement.addEventListener('click', handleClick)
      return () => {
        editorElement.removeEventListener('click', handleClick)
      }
    }
  }, [editor, isEnabled])

  const handleApprove = () => {
    console.log('handleApprove clicked')

    if (activeSuggestion) {
      console.log('activeSuggestion', activeSuggestion)

      editor.dispatchCommand(APPLY_SUGGESTION_COMMAND, {
        suggestionId: activeSuggestion.data.id,
      })
    }
  }

  const handleReject = () => {
    if (activeSuggestion) {
      editor.dispatchCommand(REJECT_SUGGESTION_COMMAND, {
        suggestionId: activeSuggestion.data.id,
      })
    }
  }

  const handleClose = () => {
    setActiveSuggestion(null)
  }

  if (!isEnabled) {
    return null
  }

  return (
    <>
      {activeSuggestion && (
        <SuggestionPopover
          position={activeSuggestion.position}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={handleClose}
        />
      )}
    </>
  )
}

function getSuggestionNodeRect(suggestionId: string): DOMRect | null {
  const element = document.querySelector(`[data-suggestion-id="${suggestionId}"]`)
  return element ? element.getBoundingClientRect() : null
}
